import { useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Download, Loader2, Paperclip, Trash2 } from 'lucide-react'
import { Button } from '@quebon/ui/components/button'
import { handleApiError } from '@/api/client'
import {
  deleteLeadDocument,
  fetchLeadDocumentDownloadUrl,
  fetchLeadDocuments,
  uploadLeadDocument,
  type LeadDocumentType,
} from '@/api/lcCrm'
import { LEAD_DOCUMENT_TYPE_LABEL, formatKstDateTime, labelOf } from './crmLabels'

/** 리드 문서로 받는 형식. 서버 허용 목록(이미지·PDF·doc/docx·hwp/hwpx)과 같다. */
const ACCEPT = '.jpg,.jpeg,.png,.webp,.pdf,.doc,.docx,.hwp,.hwpx'

interface Props {
  leadId: number
  documentType: LeadDocumentType
  /** 비우면 문서 종류 라벨을 제목으로 쓴다. */
  title?: string
  description?: string
}

/**
 * 리드 단위 원본 문서(적격성 근거·정보공개서·계약서) 한 종류를 보여주고 올리고 내려받는 블록.
 * 삭제는 논리 삭제라 서버 목록에는 남지만, 화면에는 살아 있는 것만 보여준다.
 */
export default function LeadDocumentPanel({ leadId, documentType, title, description }: Props) {
  const queryClient = useQueryClient()
  const fileRef = useRef<HTMLInputElement>(null)
  const queryKey = ['lc-crm-lead-documents', leadId, documentType]
  const heading = title ?? labelOf(LEAD_DOCUMENT_TYPE_LABEL, documentType)

  const documents = useQuery({
    queryKey,
    queryFn: () => fetchLeadDocuments(leadId, documentType),
  })

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey })
    queryClient.invalidateQueries({ queryKey: ['lc-crm-lead-history', leadId] })
  }

  const upload = useMutation({
    mutationFn: (file: File) => uploadLeadDocument(leadId, documentType, file),
    onSuccess: () => {
      toast.success(`${heading}을(를) 올렸습니다`)
      invalidate()
    },
    onError: (err) => toast.error(handleApiError(err) || '문서 업로드 실패'),
  })

  const remove = useMutation({
    mutationFn: (documentId: number) => deleteLeadDocument(documentId),
    onSuccess: () => {
      toast.success('문서를 삭제했습니다')
      invalidate()
    },
    onError: (err) => toast.error(handleApiError(err) || '문서 삭제 실패'),
  })

  const download = useMutation({
    mutationFn: (documentId: number) => fetchLeadDocumentDownloadUrl(documentId),
    onSuccess: ({ downloadUrl }) => {
      window.open(downloadUrl, '_blank', 'noopener,noreferrer')
    },
    onError: (err) => toast.error(handleApiError(err) || '내려받기 URL 발급 실패'),
  })

  const live = documents.data?.filter((item) => !item.logicalDeletedAt) ?? []

  return (
    <section className="flex flex-col gap-2 rounded-lg border border-border p-3" aria-label={heading}>
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-col">
          <span className="text-sm font-medium">{heading}</span>
          {description && <span className="text-xs text-muted-foreground">{description}</span>}
        </div>
        <input
          ref={fileRef}
          type="file"
          accept={ACCEPT}
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) upload.mutate(file)
            e.target.value = ''
          }}
        />
        <Button size="xs" variant="outline" disabled={upload.isPending} onClick={() => fileRef.current?.click()}>
          {upload.isPending ? <Loader2 className="size-3 animate-spin" /> : <Paperclip className="size-3" />}
          업로드
        </Button>
      </header>

      {documents.isLoading && <span className="text-xs text-muted-foreground">불러오는 중…</span>}
      {documents.error && <span className="text-xs text-destructive">문서 목록을 불러오지 못했습니다.</span>}
      {documents.data && live.length === 0 && !upload.isPending && (
        <span className="text-xs text-muted-foreground">올린 문서가 없습니다.</span>
      )}
      {live.length > 0 && (
        <ul className="flex flex-col gap-1">
          {live.map((item) => (
            <li
              key={item.documentId}
              className="flex flex-wrap items-center gap-2 rounded-md bg-muted px-2 py-1 text-xs"
            >
              <span className="min-w-0 flex-1 truncate" title={item.originalFilename}>
                {item.originalFilename}
              </span>
              <span className="tabular-nums text-muted-foreground">{formatFileSize(item.fileSize)}</span>
              <span className="text-muted-foreground">{formatKstDateTime(item.createdAt)}</span>
              <Button
                size="icon-xs"
                variant="ghost"
                aria-label={`${item.originalFilename} 내려받기`}
                disabled={download.isPending}
                onClick={() => download.mutate(item.documentId)}
              >
                <Download className="size-3" />
              </Button>
              <Button
                size="icon-xs"
                variant="ghost"
                aria-label={`${item.originalFilename} 삭제`}
                disabled={remove.isPending}
                onClick={() => remove.mutate(item.documentId)}
              >
                <Trash2 className="size-3" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
